import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';

const T1_DEDICATED = ['F1','F2','F3','F4','F5','F6','F7','F8','F9','F10'];

function LockIcon() {
  return <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
}
function CheckIcon() {
  return <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>;
}
function BellIcon() {
  return <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
}
function BanIcon() {
  return <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>;
}

export default function WorkshopsPage() {
  const navigate = useNavigate();
  const { workshopStates, setWorkshopStates } = useSocket();
  const { operator } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/workshops`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        data.forEach(w => { map[w.workshop] = w; });
        setWorkshopStates(map);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [setWorkshopStates]);

  // Build all 20 workshops
  const workshops = Array.from({ length: 20 }, (_, i) => {
    const id = `F${i + 1}`;
    const s = workshopStates[id] || {};
    const isDedicated = T1_DEDICATED.includes(id);
    const isShared = !isDedicated;
    const isT2 = operator === 'T2';

    // T2 cannot operate T1-dedicated shops
    const isDisabledForMe = isT2 && isDedicated;

    // Locked by someone else
    const isLockedByOther = s.status === 'busy' && s.lockedBy && s.lockedBy !== operator;

    // Active by me
    const isActiveByMe = s.status === 'busy' && s.lockedBy === operator;

    return { id, isDedicated, isShared, isDisabledForMe, isLockedByOther, isActiveByMe, lockedBy: s.lockedBy || null };
  });

  function getCardClass(w) {
    if (w.isDisabledForMe) return 'ws-card ws-disabled';
    if (w.isLockedByOther) return 'ws-card ws-locked';
    if (w.isActiveByMe) return 'ws-card ws-active-mine';
    return 'ws-card ws-available';
  }

  function handleSelect(w) {
    if (w.isDisabledForMe || w.isLockedByOther) return;
    navigate(`/alert/${w.id}`);
  }

  if (loading) return <div className="spinner" />;

  const t1Dedicated = workshops.slice(0, 10);
  const shared = workshops.slice(10, 20);

  return (
    <div>
      <div className="legend">
        <div className="leg-item"><div className="leg-dot" style={{ background: '#43a047' }} />Available</div>
        <div className="leg-item"><div className="leg-dot" style={{ background: '#fb8c00' }} />You — active</div>
        <div className="leg-item"><div className="leg-dot" style={{ background: '#e53935' }} />Locked</div>
        {operator === 'T2' && <div className="leg-item"><div className="leg-dot" style={{ background: '#c8d0dc' }} />T1 dedicated</div>}
      </div>

      {/* T1 dedicated */}
      <p className="section-label">
        F1 – F10 &nbsp;
        <span style={{ color: '#003580', fontWeight: 600 }}>
          {operator === 'T1' ? '· Your dedicated workshops' : '· Traverser-1 dedicated'}
        </span>
      </p>
      <div className="workshop-grid">
        {t1Dedicated.map(w => (
          <div key={w.id} className={getCardClass(w)} onClick={() => handleSelect(w)}>
            {!w.isDisabledForMe && <div className="ws-dedicated-pill">T1</div>}
            <div className="ws-num">{w.id}</div>
            <div className="ws-tag">
              {w.isDisabledForMe ? <><BanIcon />T1 Dedicated</> :
               w.isLockedByOther ? <><LockIcon />Locked — {w.lockedBy}</> :
               w.isActiveByMe ? <><BellIcon />You — Active</> :
               <><CheckIcon />Available</>}
            </div>
          </div>
        ))}
      </div>

      {/* Shared */}
      <p className="section-label" style={{ marginTop: 4 }}>
        F11 – F20 &nbsp;
        <span style={{ color: '#6a0dad', fontWeight: 600 }}>· Shared — T1 &amp; T2</span>
      </p>
      <div className="workshop-grid">
        {shared.map(w => (
          <div key={w.id} className={getCardClass(w)} onClick={() => handleSelect(w)}>
            <div className="ws-dedicated-pill" style={{ background: 'rgba(106,13,173,0.1)', color: '#6a0dad' }}>Shared</div>
            <div className="ws-num">{w.id}</div>
            <div className="ws-tag">
              {w.isLockedByOther ? <><LockIcon />Locked — {w.lockedBy}</> :
               w.isActiveByMe ? <><BellIcon />You — Active</> :
               <><CheckIcon />Available</>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
